(() => {
  'use strict';

  const root = window.Shopify?.routes?.root || '/';
  const currency = document.documentElement.dataset.am2Currency || 'MXN';
  const prefersReducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const money = value => new Intl.NumberFormat(document.documentElement.lang || 'es-MX', { style: 'currency', currency }).format(value / 100);
  const read = key => {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value.filter(item => typeof item === 'string' && /^[a-z0-9-]+$/.test(item)).slice(0, 30) : [];
    } catch {
      return [];
    }
  };
  const write = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  };
  const event = (name, detail = {}) => document.dispatchEvent(new CustomEvent('aftermind:interaction', { detail: { name, ...detail } }));
  const analyticsConsentGranted = () => {
    try {
      return localStorage.getItem('aftermind_cookie_consent_v1') === 'accepted';
    } catch {
      return false;
    }
  };
  const createCartAddEvent = async (form, variantId, quantity) => {
    if (!analyticsConsentGranted()) return null;
    try {
      const { CartLinesUpdateEvent } = await import('https://cdn.shopify.com/storefront/standard-events.js');
      const deferred = CartLinesUpdateEvent.createPromise();
      const target = form.closest('[data-product-handle]') || form;
      target.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise
      }));
      return { CartLinesUpdateEvent, deferred };
    } catch {
      return null;
    }
  };
  const element = (tag, text, className) => {
    const node = document.createElement(tag);
    if (text !== undefined) node.textContent = text;
    if (className) node.className = className;
    return node;
  };
  const image = (src, title) => {
    const node = element('img');
    node.src = src;
    node.alt = title || '';
    node.loading = 'lazy';
    return node;
  };
  const request = async (path, options = {}) => {
    const response = await fetch(path, { credentials: 'same-origin', ...options });
    let data;
    try {
      data = await response.json();
    } catch {
      throw new Error('No se pudo leer la respuesta. Inténtalo de nuevo.');
    }
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : typeof data.error === 'string' ? data.error : 'No se pudo completar la acción. Inténtalo de nuevo.');
    return data;
  };
  const safePath = url => {
    try {
      const value = new URL(url, location.origin);
      return value.origin === location.origin ? value.pathname + value.search : root + 'collections/all';
    } catch {
      return root + 'collections/all';
    }
  };

  const productCache = new Map();
  const product = handle => {
    if (!productCache.has(handle)) {
      productCache.set(handle, request(root + 'products/' + encodeURIComponent(handle) + '.js').catch(error => {
        productCache.delete(handle);
        throw error;
      }));
    }
    return productCache.get(handle);
  };

  let revealObserver;
  const reveal = context => {
    const targets = context.querySelectorAll('[data-reveal]');
    if (!targets.length || prefersReducedMotion() || !('IntersectionObserver' in window)) {
      targets.forEach(node => node.classList.add('am2-visible'));
      return;
    }
    document.documentElement.classList.add('am2-motion');
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('am2-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { threshold: 0.08 });
    }
    targets.forEach(node => {
      if (!node.dataset.revealReady) {
        node.dataset.revealReady = 'true';
        revealObserver.observe(node);
      }
    });
  };

  let splitObserver;
  const splitLetters = context => {
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      context.querySelectorAll('[data-split]').forEach(node => node.classList.add('am6-split-ready'));
      return;
    }
    if (!splitObserver) {
      splitObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('am6-split-ready');
          splitObserver.unobserve(entry.target);
        });
      }, { threshold: 0.14, rootMargin: '0px 0px -6% 0px' });
    }
    context.querySelectorAll('[data-split]').forEach(node => {
      if (node.dataset.splitReady) return;
      node.dataset.splitReady = 'true';
      const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      let textNode;
      while ((textNode = walker.nextNode())) textNodes.push(textNode);
      let index = 0;
      textNodes.forEach(source => {
        const fragment = document.createDocumentFragment();
        Array.from(source.nodeValue).forEach(character => {
          const letter = document.createElement('span');
          letter.className = 'am6-char';
          letter.style.setProperty('--am6-char-delay', `${Math.min(index * 18, 750)}ms`);
          letter.textContent = character === ' ' ? '\u00a0' : character;
          fragment.append(letter);
          index += 1;
        });
        source.replaceWith(fragment);
      });
      splitObserver.observe(node);
    });
  };

  let parallaxFrame = 0;
  let parallaxBound = false;
  const updateParallax = () => {
    parallaxFrame = 0;
    if (prefersReducedMotion()) return;
    const viewport = window.innerHeight || 1;
    document.querySelectorAll('[data-am2-parallax]').forEach(node => {
      const speed = Number(node.dataset.am2Parallax) || 0;
      const rect = node.getBoundingClientRect();
      const progress = (rect.top + rect.height / 2 - viewport / 2) / viewport;
      node.style.setProperty('--am2-parallax-y', `${Math.round(progress * speed * -100)}px`);
    });
  };
  const parallax = () => {
    if (!document.querySelector('[data-am2-parallax]')) return;
    if (!parallaxBound) {
      parallaxBound = true;
      const requestUpdate = () => {
        if (!parallaxFrame) parallaxFrame = requestAnimationFrame(updateParallax);
      };
      addEventListener('scroll', requestUpdate, { passive: true });
      addEventListener('resize', requestUpdate);
    }
    if (!parallaxFrame) parallaxFrame = requestAnimationFrame(updateParallax);
  };

  const updateCartCount = cart => document.querySelectorAll('[data-cart-count]').forEach(node => {
    node.textContent = cart.item_count;
  });
  let previousFocus;
  const openDialog = id => {
    const dialog = document.getElementById(id);
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    const active = document.querySelector('.am2-dialog[open]');
    if (active === dialog) return true;
    if (!active) previousFocus = document.activeElement;
    else active.close();
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    dialog.querySelector('input')?.focus();
    return true;
  };
  const closeDialog = dialog => {
    dialog?.close();
    if (!document.querySelector('.am2-dialog[open]')) {
      document.documentElement.style.overflow = '';
      previousFocus?.focus();
    }
  };
  const miniCard = item => {
    const link = element('a', undefined, 'am2-mini-card');
    link.href = safePath(item.url || root + 'products/' + item.handle);
    const src = typeof item.featured_image === 'string' ? item.featured_image : item.featured_image?.url || item.image;
    if (src) link.append(image(src, item.title));
    const info = element('div', item.title);
    if (item.price !== undefined) info.append(element('span', money(Number(item.price))));
    link.append(info);
    return link;
  };
  const syncSaves = () => {
    const saved = read('am2-saved');
    document.querySelectorAll('[data-save]').forEach(button => {
      const selected = saved.includes(button.dataset.save);
      button.setAttribute('aria-pressed', String(selected));
      button.textContent = selected ? 'Guardada' : (button.classList.contains('am2-pdp-save') ? 'Guardar esta pieza' : 'Guardar');
    });
  };
  const savedList = async () => {
    const target = document.querySelector('[data-saved-content]');
    if (!target) return;
    target.replaceChildren(element('p', 'Cargando tus piezas…'));
    const items = await Promise.allSettled(read('am2-saved').map(product));
    const cards = items.filter(item => item.status === 'fulfilled').map(item => miniCard(item.value));
    target.replaceChildren(...cards);
    if (!cards.length) target.append(element('p', 'Guarda una pieza para volver a encontrarla aquí.'));
  };
  const loyaltyPanel = async (cart, target) => {
    const panel = element('section', undefined, 'am2-cart-loyalty');
    const customer = target.dataset.loyaltyCustomer === 'true';
    const subtotal = Number(cart.total_price || 0) / 100;
    const levels = [{name: 'MIEMBRO', xp: 0}, {name: 'CÍRCULO', xp: 1000}, {name: 'ARCHIVO', xp: 2500}, {name: 'AFTER WORLD', xp: 5000}];
    const rewards = [{xp: 1000, mxn: 50}, {xp: 2000, mxn: 120}, {xp: 3500, mxn: 250}];
    const heading = element('div', undefined, 'am2-cart-loyalty__heading');
    heading.append(element('span', 'AFTERMIND XP'), element('strong', customer ? 'CARGANDO…' : 'TU PROGRESO'));
    const track = element('div', undefined, 'am2-cart-loyalty__track');
    const fill = element('span');
    track.append(fill);
    const copy = element('p', customer ? 'Consultando tu XP real…' : 'Regístrate y cada compra elegible suma a tu nivel.');
    panel.append(heading, track, copy);
    if (!customer) {
      fill.style.width = '0%';
      const meta = element('div', undefined, 'am2-cart-loyalty__meta');
      meta.append(element('span', '0 XP'), element('span', '1,000 XP · CÍRCULO'));
      const link = element('a', 'ACTIVAR AFTER WORLD →', 'am2-cart-loyalty__link');
      link.href = target.dataset.loyaltyAccountUrl || target.dataset.loyaltyWorldUrl || `${root}account`;
      panel.append(meta, link);
      return panel;
    }
    const meta = element('div', undefined, 'am2-cart-loyalty__meta');
    const rewardList = element('div', undefined, 'am2-cart-loyalty__rewards');
    const message = element('p', '', 'am2-cart-loyalty__message');
    message.hidden = true;
    panel.append(meta, rewardList, message);
    const showMessage = value => { message.hidden = false; message.textContent = value; };
    try {
      const data = await request(`${root}apps/loyalty/dashboard`);
      if (data.error) throw new Error(data.error);
      const balance = Number(data.xpBalance || 0);
      const lifetime = Number(data.xpLifetime || 0);
      const currentIndex = levels.reduce((index, level, next) => lifetime >= level.xp ? next : index, 0);
      const current = levels[currentIndex];
      const next = levels[currentIndex + 1];
      const progress = next ? Math.min(100, Math.max(0, ((lifetime - current.xp) / (next.xp - current.xp)) * 100)) : 100;
      heading.lastElementChild.textContent = `${balance.toLocaleString('es-MX')} XP`;
      fill.style.width = `${progress}%`;
      copy.textContent = next ? `${data.level || current.name} · te faltan ${(next.xp - lifetime).toLocaleString('es-MX')} XP para ${next.name}.` : 'AFTER WORLD · ya alcanzaste el nivel máximo.';
      meta.append(element('span', `${lifetime.toLocaleString('es-MX')} XP acumulados`), element('span', next ? `${next.xp.toLocaleString('es-MX')} XP · ${next.name}` : 'NIVEL MÁXIMO'));
      rewards.forEach(reward => {
        const button = element('button', undefined, 'am2-cart-loyalty__reward');
        button.type = 'button';
        const available = balance >= reward.xp && subtotal >= 499;
        button.disabled = !available;
        button.append(element('span', `${reward.xp.toLocaleString('es-MX')} XP`), element('strong', `$${reward.mxn} MXN`), element('small', subtotal < 499 ? 'Mínimo $499 MXN' : balance < reward.xp ? 'XP insuficientes' : 'CANJEAR EN LA BOLSA'));
        button.addEventListener('click', async () => {
          const original = button.innerHTML;
          button.disabled = true;
          button.textContent = 'APLICANDO…';
          try {
            const result = await request(`${root}apps/loyalty/redeem`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({xp: reward.xp})});
            if (!result.code) throw new Error(result.error || 'No se pudo aplicar la recompensa.');
            window.location.assign(`${root}discount/${encodeURIComponent(result.code)}?redirect=/cart`);
          } catch (error) {
            button.disabled = false;
            button.innerHTML = original;
            showMessage(error.message || 'No se pudo aplicar tu recompensa. Tus XP no cambiaron.');
          }
        });
        rewardList.append(button);
      });
    } catch (error) {
      heading.lastElementChild.textContent = 'XP NO DISPONIBLE';
      copy.textContent = error.message || 'No pudimos consultar tu progreso ahora.';
      fill.style.width = '0%';
    }
    return panel;
  };
  const renderCart = cart => {
    updateCartCount(cart);
    const target = document.querySelector('[data-cart-content]');
    if (!target) return;
    target.replaceChildren();
    if (!cart.items.length) {
      target.append(element('p', 'Tu bolsa está vacía. Encuentra una idea que te represente.', 'am2-empty'));
      return;
    }
    cart.items.forEach(item => {
      const row = element('div', undefined, 'am2-cart-line');
      if (item.image) row.append(image(item.image, item.product_title));
      else row.append(element('span'));
      const info = element('div');
      const link = element('a', item.product_title);
      link.href = safePath(item.url);
      info.append(link);
      if (item.variant_title && item.variant_title !== 'Default Title') info.append(element('p', item.variant_title));
      info.append(element('p', `Cantidad: ${item.quantity}`));
      const remove = element('button', 'Quitar', 'am2-remove');
      remove.type = 'button';
      remove.dataset.removeKey = item.key;
      remove.setAttribute('aria-label', `Quitar ${item.product_title}`);
      info.append(remove);
      row.append(info, element('span', money(item.final_line_price)));
      target.append(row);
    });
    const total = element('div', undefined, 'am2-total');
    total.append(element('span', 'Subtotal'), element('strong', money(cart.total_price)));
    const form = element('form');
    form.action = root + 'cart';
    form.method = 'post';
    const checkout = element('button', 'Finalizar compra →', 'am2-button');
    checkout.type = 'submit';
    checkout.name = 'checkout';
    form.append(checkout);
    target.append(total);
    loyaltyPanel(cart, target).then(panel => target.append(panel, element('p', 'Envío e impuestos, cuando correspondan, calculados al finalizar la compra.'), form));
  };
  const loadCart = async () => {
    const status = document.querySelector('[data-cart-status]');
    if (!status) return;
    status.textContent = 'Actualizando tu bolsa…';
    try {
      renderCart(await request(root + 'cart.js'));
      status.textContent = '';
    } catch (error) {
      status.textContent = `${error.message} Puedes consultar la bolsa completa.`;
    }
  };

  document.addEventListener('click', async eventObject => {
    const opener = eventObject.target.closest('[data-open]');
    if (opener) {
      if (openDialog(opener.dataset.open)) eventObject.preventDefault();
      if (opener.dataset.open === 'am2-saved') savedList();
    }
    const closer = eventObject.target.closest('[data-close]');
    if (closer) closeDialog(closer.closest('dialog'));
    if (eventObject.target.matches('.am2-dialog')) {
      const box = eventObject.target.getBoundingClientRect();
      if (eventObject.clientX < box.left || eventObject.clientX > box.right || eventObject.clientY < box.top || eventObject.clientY > box.bottom) closeDialog(eventObject.target);
    }
    const nav = eventObject.target.closest('.am2-menu a');
    if (nav) closeDialog(nav.closest('dialog'));
    const cartLink = eventObject.target.closest('[data-cart-open]');
    if (cartLink && openDialog('am2-cart')) {
      eventObject.preventDefault();
      loadCart();
      event('cart_view');
    }
    const save = eventObject.target.closest('[data-save]');
    if (save) {
      const items = read('am2-saved');
      const exists = items.includes(save.dataset.save);
      const next = exists ? items.filter(item => item !== save.dataset.save) : [save.dataset.save, ...items].slice(0, 30);
      if (write('am2-saved', next)) {
        syncSaves();
        event('wishlist', { handle: save.dataset.save, saved: !exists });
      } else {
        save.textContent = 'No se pudo guardar';
      }
    }
    const remove = eventObject.target.closest('[data-remove-key]');
    if (remove) {
      remove.disabled = true;
      const status = document.querySelector('[data-cart-status]');
      if (status) status.textContent = 'Actualizando…';
      try {
        const cart = await request(root + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: remove.dataset.removeKey, quantity: 0 }) });
        renderCart(cart);
        if (status) status.textContent = 'Prenda retirada de la bolsa.';
        event('remove_from_cart');
      } catch (error) {
        if (status) status.textContent = error.message;
        remove.disabled = false;
      }
    }
    const sticky = eventObject.target.closest('[data-sticky-buy]');
    if (sticky) {
      const form = document.querySelector('.am2-product-form');
      form?.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      form?.querySelector('select')?.focus({ preventScroll: true });
    }
  });

  document.addEventListener('submit', async eventObject => {
    const form = eventObject.target.closest('.am2-product-form');
    if (!form) return;
    eventObject.preventDefault();
    if (form.dataset.busy) return;
    const button = form.querySelector('[data-add-button]');
    const status = form.querySelector('[data-product-status]');
    const data = new FormData(form);
    if (!data.get('id')) {
      status.textContent = 'Elige tu talla antes de agregar.';
      form.querySelector('select')?.focus();
      return;
    }
    form.dataset.busy = 'true';
    button.disabled = true;
    button.textContent = 'Agregando…';
    status.textContent = '';
    const quantity = Number(data.get('quantity'));
    const standardEvent = await createCartAddEvent(form, data.get('id'), quantity);
    try {
      await request(root + 'cart/add.js', { method: 'POST', body: data });
      let cart;
      if (standardEvent) {
        cart = await request(root + 'cart.js');
        standardEvent.deferred.resolve({ cart: standardEvent.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
      }
      status.textContent = 'Tu pieza ya está en la bolsa.';
      event('add_to_cart', { variant_id: data.get('id'), quantity });
      if (openDialog('am2-cart')) {
        if (cart) renderCart(cart);
        else await loadCart();
      }
    } catch (error) {
      standardEvent?.deferred.reject(error);
      status.textContent = error.message;
    } finally {
      delete form.dataset.busy;
      button.disabled = false;
      button.textContent = 'Agregar a la bolsa →';
    }
  });

  let searchTimer;
  let searchController;
  let searchSequence = 0;
  document.addEventListener('input', eventObject => {
    if (eventObject.target.id !== 'am2-query') return;
    clearTimeout(searchTimer);
    searchController?.abort();
    const query = eventObject.target.value.trim();
    const results = document.getElementById('am2-search-results');
    const status = document.getElementById('am2-search-status');
    const sequence = ++searchSequence;
    results.replaceChildren();
    status.textContent = '';
    if (query.length < 2) return;
    searchTimer = setTimeout(async () => {
      searchController = new AbortController();
      status.textContent = 'Buscando…';
      try {
        const params = new URLSearchParams({ q: query, 'resources[type]': 'product,collection,article,page', 'resources[limit]': '6' });
        const data = await request(root + 'search/suggest.json?' + params, { signal: searchController.signal });
        if (sequence !== searchSequence) return;
        const found = data.resources?.results || {};
        const items = [
          ...(found.products || []).map(item => ({ ...item, price: Math.round(Number(item.price) * 100) })),
          ...(found.collections || []),
          ...(found.articles || []),
          ...(found.pages || [])
        ];
        results.replaceChildren(...items.map(miniCard));
        status.textContent = items.length ? `${items.length} resultados sugeridos.` : 'Sin coincidencias. Prueba con otro nombre o explora las colecciones.';
      } catch (error) {
        if (error.name !== 'AbortError' && sequence === searchSequence) status.textContent = 'Las sugerencias no están disponibles. Pulsa buscar para ver los resultados.';
      }
    }, 250);
  });

  const bindNavigation = context => {
    context.querySelectorAll('.am6-nav-menu').forEach(menu => {
      if (menu.dataset.ready) return;
      menu.dataset.ready = 'true';
      menu.addEventListener('toggle', () => {
        if (!menu.open) return;
        document.querySelectorAll('.am6-nav-menu[open]').forEach(other => {
          if (other !== menu) other.removeAttribute('open');
        });
      });
    });
  };
  const init = (context = document) => {
    reveal(context);
    splitLetters(context);
    syncSaves();
    bindNavigation(context);
    context.querySelectorAll('.am2-dialog').forEach(dialog => {
      if (dialog.dataset.ready) return;
      dialog.dataset.ready = 'true';
      dialog.addEventListener('close', () => {
        if (!document.querySelector('.am2-dialog[open]')) document.documentElement.style.overflow = '';
      });
    });
    context.querySelectorAll('[data-product-handle]').forEach(section => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      const handle = section.dataset.productHandle;
      const old = read('am2-recent');
      write('am2-recent', [handle, ...old.filter(item => item !== handle)].slice(0, 12));
      event('product_view', { handle });
      const variantsSource = section.querySelector('[data-product-variants]');
      const variants = variantsSource ? JSON.parse(variantsSource.textContent) : [];
      const select = section.querySelector('[data-variant-select]');
      select?.addEventListener('change', () => {
        const variant = variants.find(item => String(item.id) === select.value);
        if (!variant) return;
        section.querySelector('[data-product-price]').textContent = money(variant.price);
        const compare = section.querySelector('[data-product-compare]');
        compare.hidden = !(variant.compare_at_price > variant.price);
        compare.textContent = variant.compare_at_price ? money(variant.compare_at_price) : '';
        section.querySelector('[data-product-stock]').textContent = variant.available ? 'Disponible' : 'Agotado';
        const button = section.querySelector('[data-add-button]');
        button.disabled = !variant.available;
        button.textContent = variant.available ? 'Agregar a la bolsa →' : 'Agotado';
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
        event('variant_selected', { variant_id: variant.id });
      });
      const gallery = section.querySelector('[data-gallery]');
      if (gallery) {
        const slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
        const thumbnails = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
        const count = gallery.querySelector('[data-gallery-count]');
        let index = 0;
        const showSlide = nextIndex => {
          if (!slides.length) return;
          index = (nextIndex + slides.length) % slides.length;
          slides.forEach((slide, slideIndex) => {
            slide.hidden = slideIndex !== index;
          });
          thumbnails.forEach((thumbnail, thumbnailIndex) => {
            thumbnail.setAttribute('aria-selected', String(thumbnailIndex === index));
          });
          if (count) count.textContent = `${index + 1} / ${slides.length}`;
        };
        gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => showSlide(index - 1));
        gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => showSlide(index + 1));
        thumbnails.forEach((thumbnail, thumbnailIndex) => thumbnail.addEventListener('click', () => showSlide(thumbnailIndex)));
        gallery.querySelector('[data-gallery-viewport]')?.addEventListener('keydown', eventObject => {
          if (eventObject.key === 'ArrowLeft') {
            eventObject.preventDefault();
            showSlide(index - 1);
          }
          if (eventObject.key === 'ArrowRight') {
            eventObject.preventDefault();
            showSlide(index + 1);
          }
        });
        let touchStartX;
        const viewport = gallery.querySelector('[data-gallery-viewport]');
        viewport?.addEventListener('touchstart', eventObject => {
          touchStartX = eventObject.changedTouches[0]?.clientX;
        }, { passive: true });
        viewport?.addEventListener('touchend', eventObject => {
          const endX = eventObject.changedTouches[0]?.clientX;
          if (typeof touchStartX !== 'number' || typeof endX !== 'number' || Math.abs(endX - touchStartX) < 36) return;
          showSlide(endX < touchStartX ? index + 1 : index - 1);
        }, { passive: true });
        showSlide(0);
      }
      const form = section.querySelector('.am2-product-form');
      const sticky = section.querySelector('.am2-sticky-buy');
      if (form && sticky && 'IntersectionObserver' in window) {
        new IntersectionObserver(entries => {
          const entry = entries[0];
          sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
        }, { threshold: 0 }).observe(form);
      }
    });
    context.querySelectorAll('[data-recommendations]').forEach(async section => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      try {
        const response = await fetch(section.dataset.recommendations);
        if (!response.ok) return;
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = documentFragment.querySelector('[data-recommendations]');
        if (content) {
          section.innerHTML = content.innerHTML;
          reveal(section);
          splitLetters(section);
          syncSaves();
        }
      } catch {
        return;
      }
    });
    context.querySelectorAll('[data-recent-section]').forEach(async section => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      const current = document.querySelector('[data-product-handle]')?.dataset.productHandle;
      const handles = read('am2-recent').filter(handle => handle !== current).slice(0, 4);
      if (!handles.length) return;
      const products = await Promise.allSettled(handles.map(product));
      const items = products.filter(item => item.status === 'fulfilled').map(item => item.value);
      if (!items.length) return;
      const grid = section.querySelector('[data-recent-grid]');
      grid.replaceChildren(...items.map(item => {
        const card = element('article', undefined, 'am2-card');
        const link = element('a', undefined, 'am2-card-media');
        link.href = safePath(item.url);
        if (item.featured_image) link.append(image(item.featured_image, item.title));
        const info = element('div', undefined, 'am2-card-info');
        const title = element('a', item.title);
        title.href = safePath(item.url);
        info.append(title, element('span', money(item.price)));
        card.append(link, info);
        return card;
      }));
      section.hidden = false;
      reveal(section);
    });
    parallax();
  };

  init();
  document.addEventListener('shopify:section:load', eventObject => init(eventObject.target));
  window.addEventListener('storage', syncSaves);
})();
